import React, { useState, useEffect, useCallback } from "react";
import { useColors } from "../../../contexts/ColorContext";
import { Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import api from "../../../services/api";

export default function NovoAgendamento({ usuario, idClienteMaster }) {
  const { colors } = useColors();
  const { primaryColor, secondaryColor, cardBackgroundColor, cardTextColor, buttonColor, buttonTextColor } = colors;
  const [form, setForm] = useState({
    clienteId: null,
  });

  // autocomplete - cliente
  const [clienteQuery, setClienteQuery] = useState("");
  const [clienteSuggestions, setClienteSuggestions] = useState([]);
  const [clienteSelecionadoNome, setClienteSelecionadoNome] = useState("");

  // serviços
  const [servicoSuggestions, setServicoSuggestions] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  // data / horários
  const [selectedDate, setSelectedDate] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);

  // loading / erros
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingServicos, setLoadingServicos] = useState(false);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [error, setError] = useState(null);

  // debounce
  const debounce = (fn, delay = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), delay);
    };
  };

  // buscar clientes
  const fetchClientes = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 3) {
        setClienteSuggestions([]);
        return;
      }
      setLoadingClientes(true);
      try {
        const res = await api.get(
          `/Clientes/search?idClienteMaster=${idClienteMaster}&query=${encodeURIComponent(query)}`
        );
        setClienteSuggestions(res.data || []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      } finally {
        setLoadingClientes(false);
      }
    }, 300),
    [idClienteMaster]
  );

  // handlers cliente
  const handleClienteChange = (e) => {
    const q = e.target.value;
    setClienteQuery(q);
    setForm((prev) => ({ ...prev, clienteId: null }));
    setClienteSelecionadoNome("");
    fetchClientes(q);
  };

  const selectCliente = (cliente) => {
    setForm((prev) => ({ ...prev, clienteId: cliente.id }));
    setClienteQuery(cliente.nome);
    setClienteSelecionadoNome(cliente.nome);
    setClienteSuggestions([]);
  };

  // carregar serviços
  useEffect(() => {
    const loadServices = async () => {
      setLoadingServicos(true);
      try {
        const res = await api.get(`/Servicos/funcionario/${usuario.id}`);
        setServicoSuggestions(res.data || []);
      } catch (err) {
        console.error("Erro ao carregar serviços:", err);
      } finally {
        setLoadingServicos(false);
      }
    };

    if (usuario?.id) loadServices();
  }, [usuario]);

  // horários disponíveis
  useEffect(() => {
    const shouldFetch = selectedService && selectedDate && usuario?.id && idClienteMaster;

    if (!shouldFetch) {
      setAvailableTimes([]);
      setSelectedTime(null);
      return;
    }

    const fetchHorarios = async () => {
      setLoadingHorarios(true);
      setAvailableTimes([]);
      setSelectedTime(null);
      setError(null);

      try {
        const res = await api.get("/Horarios/disponiveis", {
          params: {
            idClienteMaster,
            idFuncionario: usuario.id,
            idServico: selectedService.id,
            from: selectedDate,
            to: selectedDate,
          },
        });

        const times = (res.data || [])
          .map((x, i) => ({
            id: i,
            hora: (x.hora ?? x.Hora ?? x.HoraDisponivel ?? "")
              .toString()
              .substring(0, 5),
          }))
          .filter((t) => t.hora);

        setAvailableTimes(times);
      } catch (err) {
        console.error("Erro ao buscar horários:", err);
        setError("Erro ao buscar horários disponíveis.");
      } finally {
        setLoadingHorarios(false);
      }
    };

    fetchHorarios();
  }, [selectedService, selectedDate, usuario, idClienteMaster]);

  // criar agendamento
  const criarAgendamento = async () => {
    try {
      if (!form.clienteId) return window.globalAlert("Selecione um cliente.", "info");
      if (!selectedService) return window.globalAlert("Selecione um serviço.", "info");
      if (!selectedDate) return window.globalAlert("Escolha uma data.", "info");
      if (!selectedTime) return window.globalAlert("Escolha um horário.", "info");

      const dataIso = `${selectedDate}T${selectedTime}:00`;

      await api.post("/Agendamentos", {
        ClienteMasterId: idClienteMaster,
        ClienteId: form.clienteId,
        ServicoId: selectedService.id,
        FuncionarioId: usuario.id,
        DataHora: dataIso,
      });

      window.globalAlert("Agendamento criado com sucesso!", "success");

      // reset
      limparCampos();
    } catch (err) {
      console.error(err);
      window.globalAlert(err.response?.data?.message || "Erro ao criar agendamento.", "error");
    }
  };

  // função de limpar corretamente
  const limparCampos = () => {
    setForm({ clienteId: null });
    setClienteQuery("");
    setClienteSelecionadoNome("");
    setSelectedService(null);
    setSelectedDate("");
    setAvailableTimes([]);
    setSelectedTime(null);
    setError(null);
  };

  return (
    <div className="d-flex justify-content-center align-items-start w-100">
      <Card className="p-4 shadow-sm w-100" style={{ backgroundColor: cardBackgroundColor, color: cardTextColor }}>
        <h5>Novo Agendamento</h5>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form style={{ position: "relative" }}>
          {/* Cliente */}
          <Form.Group className="mb-3" style={{ position: "relative" }}>
            <Form.Label>Cliente</Form.Label>
            <Form.Control
              type="text"
              value={clienteQuery}
              onChange={handleClienteChange}
              placeholder="Digite o nome do cliente..."
            />

            {loadingClientes && <Spinner size="sm" />}

            {clienteSuggestions.length > 0 && (
              <ul className="list-group position-absolute w-100" style={{ zIndex: 1000 }}>
                {clienteSuggestions.map((c) => (
                  <li
                    key={c.id}
                    className="list-group-item list-group-item-action"
                    onClick={() => selectCliente(c)}
                    style={{ cursor: "pointer", backgroundColor: cardBackgroundColor, color: cardTextColor }}
                  >
                    {c.nome}
                  </li>
                ))}
              </ul>
            )}
          </Form.Group>

          {/* Serviço */}
          <Form.Group className="mb-3">
            <Form.Label>Serviço</Form.Label>

            {loadingServicos ? (
              <Spinner size="sm" />
            ) : (
              <Form.Select
                value={selectedService?.id || ""}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const servico = servicoSuggestions.find((s) => s.id === id);
                  setSelectedService(servico || null);
                }}
              >
                <option value="">Selecione um serviço...</option>
                {servicoSuggestions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome} {s.duracaoMinutos ? `(${s.duracaoMinutos} min)` : ""}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          {/* Data */}
          <Form.Group className="mb-3">
            <Form.Label>Data</Form.Label>
            <Form.Control
              type="date"
              value={selectedDate || ""}
              onChange={(e) => setSelectedDate(e.target.value)}
              onFocus={(e) => e.target.showPicker && e.target.showPicker()}
            />
          </Form.Group>

          {/* Horários */}
          <Form.Group className="mb-3">
            <Form.Label>Horários Disponíveis</Form.Label>

            {loadingHorarios ? (
              <Spinner />
            ) : availableTimes.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {availableTimes.map((t) => (
                  <Button
                    key={t.id}
                    size="sm"
                    variant={selectedTime === t.hora ? "success" : "outline-dark"}
                    onClick={() => setSelectedTime(t.hora)}
                    style={
                      selectedTime === t.hora
                        ? { backgroundColor: buttonColor, borderColor: buttonColor, color: buttonTextColor }
                        : { borderColor: primaryColor, color: primaryColor }
                    }
                  >
                    {t.hora}
                  </Button>
                ))}
              </div>
            ) : (
              <small className="text-muted">Selecione serviço e data para ver horários.</small>
            )}
          </Form.Group>

          {/* Botões */}
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={criarAgendamento}
              style={{ backgroundColor: primaryColor, borderColor: primaryColor, color: buttonTextColor }}
            >
              Criar
            </Button>

            <Button
              variant="secondary"
              onClick={limparCampos}
              style={{ backgroundColor: secondaryColor, borderColor: secondaryColor, color: buttonTextColor }}
            >
              Limpar
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
}
